import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { RISK_MESSAGE_COLOR_MAP } from "../../constants";
import { getRiskMessageLabel } from "../../helpers";
import SegmentedRiskGauge from "../../shared/SegmentedRiskGauge/SegmentedRiskGauge";

const DEFAULT_RISK_COLOR = "#9E9E9E";
const MINI_SIZE = 46;
const GAP = 8;

function MiniRiskCircle({ risk, message, binCount, size = MINI_SIZE }) {
  return (
    <Box sx={{ display: "inline-flex", flexShrink: 0 }}>
      <SegmentedRiskGauge
        risk={risk}
        message={message}
        binCount={binCount}
        size={size}
        thickness={5}
        labelVariant="caption"
      />
    </Box>
  );
}

/**
 * Row of per-polygon mini risk circles for multi-polygon assessments. Renders
 * as many circles as fit on one row (measured via ResizeObserver); the rest
 * collapse into an "N+" circle that opens a dialog listing every polygon.
 */
function PolygonRiskCircles({ polygons, binCount }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(polygons.length);
  const [dialogOpen, setDialogOpen] = useState(false);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const compute = () => {
      const width = node.clientWidth;
      if (!width) return;
      const perItem = MINI_SIZE + GAP;
      // All circles fit — no overflow chip needed.
      const fitWithoutChip = Math.floor((width + GAP) / perItem);
      if (fitWithoutChip >= polygons.length) {
        setVisibleCount(polygons.length);
        return;
      }
      // Reserve one slot for the "N+" chip.
      setVisibleCount(Math.max(1, fitWithoutChip - 1));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(node);
    return () => observer.disconnect();
  }, [polygons.length]);

  if (!polygons.length) return null;

  const visible = polygons.slice(0, visibleCount);
  const hiddenCount = polygons.length - visible.length;

  const getPolygonTitle = (polygon) =>
    t("descriptionDict.fields.polygonTitle", { index: polygon.index });
  const getColor = (polygon) =>
    (polygon.message != null && RISK_MESSAGE_COLOR_MAP[polygon.message]) ||
    DEFAULT_RISK_COLOR;

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: `${GAP}px`,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {visible.map((polygon) => {
          const messageLabel = getRiskMessageLabel(polygon.message, t);
          const tooltip = messageLabel
            ? `${getPolygonTitle(polygon)} — ${messageLabel}`
            : getPolygonTitle(polygon);

          return (
            <Tooltip key={polygon.index} title={tooltip}>
              <Box sx={{ display: "inline-flex" }}>
                <MiniRiskCircle
                  risk={polygon.risk}
                  message={polygon.message}
                  binCount={binCount}
                />
              </Box>
            </Tooltip>
          );
        })}

        {hiddenCount > 0 ? (
          <Tooltip title={t("risksMap.polygon.polygonRisksTitle")}>
            <ButtonBase
              onClick={() => setDialogOpen(true)}
              aria-label={t("risksMap.polygon.polygonRisksTitle")}
              sx={{
                width: MINI_SIZE,
                height: MINI_SIZE,
                borderRadius: "50%",
                border: "2px dashed",
                borderColor: "text.disabled",
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {hiddenCount}+
            </ButtonBase>
          </Tooltip>
        ) : null}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("risksMap.polygon.polygonRisksTitle")}
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="small"
            aria-label={t("common.close")}
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {polygons.map((polygon) => {
              const color = getColor(polygon);
              const messageLabel = getRiskMessageLabel(polygon.message, t);

              return (
                <Stack
                  key={polygon.index}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                >
                  <MiniRiskCircle
                    risk={polygon.risk}
                    message={polygon.message}
                    binCount={binCount}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {getPolygonTitle(polygon)}
                    </Typography>
                    {messageLabel ? (
                      <Typography variant="caption" style={{ color }}>
                        {messageLabel}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PolygonRiskCircles;
